import type { BoardShape, Cell, CellCoord, Direction, RuleTile, TileFitViolation } from '../types';

/** Cells along each side of the board. */
export const BOARD_SIZE = 11;

const CORNERS: readonly (readonly [number, number])[] = [
  [0, 0],
  [0, 1],
  [1, 0],
  [0, 9],
  [0, 10],
  [1, 10],
  [9, 0],
  [10, 0],
  [10, 1],
  [9, 10],
  [10, 9],
  [10, 10],
];

/** The cells each shape carves out of an 11 × 11 grid. `classic` has none. */
export const SHAPE_HOLES: Readonly<Record<BoardShape, readonly (readonly [number, number])[]>> = {
  classic: [],
  riverstone: CORNERS,
  starfall: [...CORNERS, [0, 5], [5, 0], [5, 10], [10, 5]],
};

/** Every cell of a `size` × `size` board, row by row, with the shape's holes marked. */
export function boardCells(shape: BoardShape, size = BOARD_SIZE): Cell[] {
  const holes = new Set(SHAPE_HOLES[shape].map(([row, col]) => `${row},${col}`));
  const cells: Cell[] = [];
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      cells.push({ row, col, hole: holes.has(`${row},${col}`) });
    }
  }
  return cells;
}

/** The centre cell: the start square unless a game draws one at random. */
export const centreCell = (size = BOARD_SIZE): CellCoord => ({ row: Math.floor(size / 2), col: Math.floor(size / 2) });

const DIRECTION_STEP: Readonly<Record<Direction, { dr: number; dc: number }>> = {
  h: { dr: 0, dc: 1 },
  v: { dr: 1, dc: 0 },
  dr: { dr: 1, dc: 1 },
  ur: { dr: -1, dc: 1 },
};

/** How a cell advances to the next along each direction. */
export const directionStep = (dir: Direction): { dr: number; dc: number } => DIRECTION_STEP[dir];

export const isDiagonal = (dir: Direction): boolean => dir === 'dr' || dir === 'ur';

/**
 * The cells a tile covers, one per character, stepping from its anchor along its direction. Every direction advances
 * the column, so the anchor is always the leftmost cell; `ur` is the one direction that climbs.
 */
export function cellsCovered(tile: Pick<RuleTile, 'row' | 'col' | 'letter' | 'dir'>): CellCoord[] {
  const length = tile.letter.length || 1;
  const { dr, dc } = DIRECTION_STEP[tile.dir];
  return Array.from({ length }, (_, i) => ({ row: tile.row + dr * i, col: tile.col + dc * i }));
}

/**
 * Why `tile` can't lie where it is, or `null` when it can: every cell it covers is on the grid and off the holes, no
 * other tile in `others` holds one of them, and with `diagonals` off it is no cluster tile on a diagonal. A tile in
 * `others` with the same id is the tile itself, and is skipped.
 */
export function tileFit(
  tile: RuleTile,
  shape: BoardShape,
  others: RuleTile[],
  diagonals: boolean,
  size = BOARD_SIZE,
): TileFitViolation | null {
  // With diagonals off a `dr`/`ur` run spells nothing, so a cluster tile turned onto one could never read as itself.
  if (!diagonals && tile.letter.length > 1 && isDiagonal(tile.dir)) return 'multi-char-on-diagonal';

  const covered = cellsCovered(tile);
  const holes = new Set(SHAPE_HOLES[shape].map(([row, col]) => `${row},${col}`));
  for (const { row, col } of covered) {
    if (row < 0 || row >= size || col < 0 || col >= size) return 'out-of-bounds';
    if (holes.has(`${row},${col}`)) return 'overlaps-hole';
  }

  const occupied = new Set(
    others
      .filter((other) => other.id !== tile.id)
      .flatMap((other) => cellsCovered(other).map((c) => `${c.row},${c.col}`)),
  );
  return covered.some((c) => occupied.has(`${c.row},${c.col}`)) ? 'collision' : null;
}
